import { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "zuntria_install_dismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [showAndroidManual, setShowAndroidManual] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (standalone) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissedAt < 1000 * 60 * 60 * 24 * 14) return;

    let gotPrompt = false;
    const onPrompt = (e) => {
      e.preventDefault();
      gotPrompt = true;
      setDeferred(e);
      setShowAndroidManual(false);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const ua = window.navigator.userAgent;
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|chrome/i.test(ua);
    const isAndroid = /android/i.test(ua);
    let iosTimer;
    if (isIos && isSafari) {
      // Delay so first-screen CTAs are never blocked on arrival
      iosTimer = setTimeout(() => { setShowIos(true); setVisible(true); }, 12000);
    }
    let timer;
    if (isAndroid) {
      timer = setTimeout(() => {
        if (!gotPrompt) {
          setShowAndroidManual(true);
          setVisible(true);
        }
      }, 12000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (timer) clearTimeout(timer);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    else dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 z-[60] rounded-2xl border border-primary/40 bg-card p-3.5 shadow-2xl md:left-auto md:right-6 md:w-96"
         style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
         data-testid="install-prompt">
      <button onClick={dismiss} className="absolute right-2.5 top-2.5 rounded-full p-1.5 text-muted-foreground hover:bg-secondary" aria-label="Dismiss" data-testid="install-dismiss">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold">Add Zuntria to your home screen</div>
          {showIos ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Open <strong>zuntria.co.uk</strong> in <strong>Safari</strong>, tap <Share className="inline h-3.5 w-3.5" /> <strong>Share</strong>, then{" "}
              <PlusSquare className="inline h-3.5 w-3.5" /> <strong>Add to Home Screen</strong> — Zuntria opens full-screen like a real app.
            </p>
          ) : showAndroidManual ? (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Open <strong>zuntria.co.uk</strong> in <strong>Chrome</strong>, tap the <strong>⋮ menu</strong>, then{" "}
              <strong>Add to Home screen</strong> (or <strong>Install app</strong>) — Zuntria opens full-screen like a real app.
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">One-tap access to your streak, leads and money tools — just like an app.</p>
          )}
          {!showIos && !showAndroidManual && (
            <Button size="sm" onClick={install} className="mt-2.5 h-9" data-testid="install-btn">
              Install app
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
